import { Bell, ChevronDown, Globe, Grid3x3, LogOut, Menu, Search, Sun, Moon, Check, UserRound, LayoutDashboard, Users, Share2, ArrowDownToLine, ArrowUpFromLine, ShieldCheck, LineChart, Layers } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Wallet } from "lucide-react";
import { useTraderDashboard } from "@/hooks/useTrader";
import { useLogout, useMe } from "@/hooks/useAuth";
import { useAdminNotifications } from "@/hooks/useMisc";
import { useTheme } from "@/hooks/useTheme";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useShellNav } from "./AppShell";
import { CommandPalette } from "./CommandPalette";

const ICON_BTN =
  "grid h-9 w-9 place-items-center rounded-[10px] text-fg-muted transition hover:bg-fg/[0.07] hover:text-fg";

const PRIORITY_DOT: Record<string, string> = {
  critical: "bg-danger", high: "bg-warning", normal: "bg-primary", low: "bg-fg-subtle",
};

function NotificationBell() {
  const { data } = useAdminNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  const rows = data?.rows ?? [];
  const count = data?.totals.all ?? 0;

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
        className={cn(ICON_BTN, "relative")}
      >
        <Bell className="h-[18px] w-[18px]" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground ring-2 ring-bg num">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 z-50 rounded-2xl border border-border bg-bg-elevated shadow-pop animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 overflow-hidden">
          <div className="px-3 py-2 border-b border-border/50 text-sm font-semibold flex items-center justify-between">
            Notifications <span className="text-[11px] text-fg-subtle font-normal">{count} pending</span>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-border/30">
            {rows.length === 0 ? (
              <p className="px-3 py-8 text-center text-xs text-fg-subtle">You're all caught up.</p>
            ) : (
              rows.slice(0, 15).map((n) => (
                <button
                  key={n.id}
                  onClick={() => { setOpen(false); nav(n.link); }}
                  className="w-full text-left px-3 py-2 hover:bg-fg/[0.05] transition flex items-start gap-2"
                >
                  <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${PRIORITY_DOT[n.priority] ?? "bg-primary"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{n.title}</p>
                    <p className="text-[11px] text-fg-subtle truncate">{n.body}</p>
                  </div>
                </button>
              ))
            )}
          </div>
          <button
            onClick={() => { setOpen(false); nav("/admin/notifications"); }}
            className="w-full px-3 py-2 text-center text-xs text-primary hover:bg-fg/[0.05] border-t border-border/50"
          >
            View all
          </button>
        </div>
      )}
    </div>
  );
}

const LANGS: { code: string; label: string; rtl?: boolean }[] = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية", rtl: true },
  { code: "ur", label: "اردو", rtl: true },
  { code: "hi", label: "हिन्दी" },
  { code: "es", label: "Español" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "zh", label: "中文" },
];

function applyLang(code: string) {
  const l = LANGS.find((x) => x.code === code) ?? LANGS[0];
  document.documentElement.lang = l.code;
  document.documentElement.dir = l.rtl ? "rtl" : "ltr";
}

function LanguageMenu() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("fx_lang") || "en");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { applyLang(lang); }, []); // eslint-disable-line
  useEffect(() => {
    function onClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const pick = (code: string) => {
    localStorage.setItem("fx_lang", code); applyLang(code); setLang(code); setOpen(false);
    if (code !== lang) window.location.reload(); // re-render every translated string
  };
  const active = LANGS.find((l) => l.code === lang) ?? LANGS[0];
  return (
    <div className="relative" ref={ref}>
      <button aria-label="Language" title={`Language: ${active.label}`} onClick={() => setOpen((v) => !v)}
        className={ICON_BTN}>
        <Globe className="h-[18px] w-[18px]" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 z-50 rounded-2xl border border-border bg-bg-elevated shadow-pop animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 overflow-hidden">
          <div className="px-3 py-2 border-b border-border/50 text-[11px] uppercase tracking-wider text-fg-subtle">Language</div>
          {LANGS.map((l) => (
            <button key={l.code} onClick={() => pick(l.code)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-fg/[0.05] transition flex items-center justify-between">
              <span>{l.label}{l.rtl && <span className="ml-1.5 text-[9px] uppercase text-fg-subtle">RTL</span>}</span>
              {lang === l.code && <Check className="h-4 w-4 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const APPS: { label: string; to: string; Icon: typeof Users }[] = [
  { label: "Dashboard", to: "/admin/dashboard", Icon: LayoutDashboard },
  { label: "Clients", to: "/admin/manage-client/trader-admin", Icon: Users },
  { label: "Partners", to: "/admin/ib/admin-report", Icon: Share2 },
  { label: "Deposits", to: "/admin/finance/admin-deposit", Icon: ArrowDownToLine },
  { label: "Withdrawals", to: "/admin/finance/admin-withdraw", Icon: ArrowUpFromLine },
  { label: "KYC", to: "/admin/kyc/request", Icon: ShieldCheck },
  { label: "MT5", to: "/admin/mt5/connection", Icon: LineChart },
  { label: "Groups", to: "/admin/groups/ib", Icon: Layers },
];

function AppsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();
  useEffect(() => {
    function onClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button aria-label="Apps" title="Quick access" onClick={() => setOpen((v) => !v)}
        className={ICON_BTN}>
        <Grid3x3 className="h-[18px] w-[18px]" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 z-50 rounded-2xl border border-border bg-bg-elevated shadow-pop animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 p-2">
          <div className="px-1.5 py-1 text-[11px] uppercase tracking-wider text-fg-subtle">Quick access</div>
          <div className="grid grid-cols-3 gap-1">
            {APPS.map(({ label, to, Icon }) => (
              <button key={to} onClick={() => { setOpen(false); nav(to); }}
                className="flex flex-col items-center gap-1.5 rounded-xl p-2.5 hover:bg-fg/[0.05] transition">
                <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="h-[18px] w-[18px]" /></span>
                <span className="text-[11px] text-fg-muted text-center leading-tight">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function useCurrentPage() {
  const { pathname } = useLocation();
  const flat = useShellNav();
  // Longest nav path that prefixes the URL wins, so nested detail pages
  // still name their parent section.
  const match = flat
    .filter((e) => pathname === e.to || pathname.startsWith(e.to + "/"))
    .sort((a, b) => b.to.length - a.to.length)[0];
  if (match) return { title: match.label, trail: match.group };
  const segs = pathname.split("/").filter(Boolean);
  const pretty = (s: string) => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return { title: pretty(segs[segs.length - 1] ?? "Home"), trail: segs.length > 1 ? pretty(segs[segs.length - 2]) : undefined };
}

const ROLE_LABEL: Record<string, string> = {
  trader: "Client", ib: "Partner", admin: "Administrator", manager: "Manager", platform_admin: "Platform",
};

function UserMenu() {
  const { data: me } = useMe();
  const logout = useLogout();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const profileTo =
    me?.role === "admin" ? "/admin/profile" :
    me?.role === "trader" || me?.role === "ib" ? "/trader/profile" : null;
  const name = me?.fullName ?? me?.email ?? "Account";
  const initials = name.split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2.5 rounded-xl py-1 pl-1 pr-2 transition hover:bg-fg/[0.05]"
      >
        <span className="relative grid h-9 w-9 place-items-center rounded-xl bg-grad-primary text-[12px] font-bold text-primary-foreground shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.3)]">
          {initials}
          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-bg animate-pulse-dot" />
        </span>
        <span className="hidden md:flex flex-col items-start leading-tight">
          <span className="max-w-[140px] truncate text-[13px] font-semibold text-fg">{name}</span>
          <span className="text-[11px] text-fg-subtle">{ROLE_LABEL[me?.role ?? ""] ?? me?.role ?? "—"}</span>
        </span>
        <ChevronDown className={cn("hidden md:block h-3.5 w-3.5 text-fg-subtle transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-64 z-50 rounded-2xl border border-border bg-bg-elevated p-1.5 shadow-pop animate-in fade-in-0 zoom-in-95 slide-in-from-top-1">
          <div className="px-3 py-2.5">
            <p className="text-sm font-semibold text-fg truncate">{name}</p>
            <p className="text-xs text-fg-subtle truncate">{me?.email}</p>
          </div>
          <div className="my-1 h-px bg-border" />
          {profileTo && (
            <button role="menuitem" onClick={() => { setOpen(false); nav(profileTo); }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-fg-muted transition hover:bg-fg/[0.05] hover:text-fg">
              <UserRound className="h-4 w-4" /> My profile
            </button>
          )}
          <button role="menuitem"
            onClick={async () => { setOpen(false); await logout.mutateAsync(); nav("/login"); }}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-danger transition hover:bg-danger/10">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** [E1] Main-wallet balance pill for the trader / IB top bar (trendx Topbar.tsx). */
function WalletPill() {
  const dash = useTraderDashboard();
  const w = dash.data?.wallet;
  const balance = w ? Number(w.balance) - Number(w.heldBalance ?? 0) : dash.data ? 0 : null;
  return (
    <Link
      to="/trader/dashboard"
      title="Available wallet balance"
      className="hidden sm:inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-bg-sunken/40 px-3 text-xs font-semibold text-fg transition hover:border-primary/40"
    >
      <span className="grid h-6 w-6 place-items-center rounded-lg bg-primary/12 text-primary"><Wallet className="h-3.5 w-3.5" /></span>
      <span className="num">
        {balance == null ? "…" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(balance)}
      </span>
    </Link>
  );
}

export function Topbar({ onMenuToggle }: { onMenuToggle?: () => void } = {}) {
  const { resolved, toggle } = useTheme();
  const { data: meData } = useMe();
  const isClient = meData?.role === "trader" || meData?.role === "ib";
  const { title, trail } = useCurrentPage();
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen((v) => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <header className="sticky top-0 z-30 px-4 pt-3 sm:px-6 lg:px-8">
      <div className="glass flex h-[60px] items-center gap-3 rounded-2xl px-3 sm:px-4">
        {onMenuToggle && (
          <button onClick={onMenuToggle} aria-label="Open menu" className={cn(ICON_BTN, "lg:hidden")}>
            <Menu className="h-5 w-5" />
          </button>
        )}

        <div className="min-w-0 flex-1">
          {trail && <p className="truncate text-[11px] font-medium text-fg-subtle">{trail}</p>}
          <p className={cn("truncate font-display font-semibold tracking-tight text-fg", trail ? "text-[15px] leading-tight" : "text-base")}>{title}</p>
        </div>

        <button
          onClick={() => setPaletteOpen(true)}
          className="hidden md:flex h-10 w-[min(340px,30vw)] items-center gap-2.5 rounded-xl border border-border bg-bg-sunken/60 px-3 text-sm text-fg-subtle transition hover:border-border-strong hover:text-fg-muted"
        >
          <Search className="h-4 w-4" />
          <span className="flex-1 text-left">{t("Search anything…")}</span>
          <span className="flex items-center gap-1"><kbd className="kbd">{isMac ? "⌘" : "Ctrl"}</kbd><kbd className="kbd">K</kbd></span>
        </button>
        <button onClick={() => setPaletteOpen(true)} aria-label="Search" className={cn(ICON_BTN, "md:hidden")}>
          <Search className="h-[18px] w-[18px]" />
        </button>

        {isClient && <WalletPill />}

        <div className="hidden sm:flex items-center gap-0.5 rounded-xl border border-border/80 bg-bg-sunken/40 p-0.5">
          <button
            onClick={toggle}
            aria-label={resolved === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={resolved === "dark" ? "Light mode" : "Dark mode"}
            className={ICON_BTN}
          >
            {resolved === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
          <LanguageMenu />
          <AppsMenu />
          <NotificationBell />
        </div>
        <div className="sm:hidden"><NotificationBell /></div>

        <div className="mx-1 hidden h-7 w-px bg-border sm:block" />
        <UserMenu />
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </header>
  );
}
